import { createFileRoute, Link } from "@tanstack/react-router";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Copy,
  Gift,
  Landmark,
  Loader2,
  Megaphone,
  Users,
  Lock,
  Wallet,
  ArrowRightLeft,
  BadgePercent,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  convertCommission,
  AFFILIATE_PAGE_SIZE,
  createCampaignCode,
  deleteSavedBankAccount,
  getAffiliateCommissions,
  getAffiliatePayouts,
  getAffiliateReferrals,
  bindMyReferrer,
  clearStoredReferral,
  getStoredReferralInfo,
  referrerErrorReason,
  getSavedBankAccounts,
  listCampaignCodes,
  requestAffiliatePayout,
  setAffiliateCode,
  type AffiliateCommission,
  type CommissionSource,
  type AffiliateMe,
  type AffiliatePayout,
  type AffiliateReferral,
  type CampaignCode,
  type SavedBankAccount,
} from "@/lib/boide-api";
import { loadAffiliateMe } from "@/lib/bootstrap";

export const Route = createFileRoute("/_authenticated/gioi-thieu")({
  head: () => ({
    meta: [
      { title: "Giới Thiệu & Hoa Hồng - Affiliate Bói Đê" },
      {
        name: "description",
        content:
          "Chia sẻ link giới thiệu Bói Đê, nhận 10% hoa hồng trọn đời mỗi lần bạn bè nạp tiền. Lấy mã ngay!",
      },
    ],
  }),
  component: GioiThieuPage,
});

const money = (n: number) => `${(n || 0).toLocaleString("vi-VN")}đ`;

const LOGS_PER_PAGE = 10;
const affiliateInputClass =
  "border-border/60 bg-background/70 shadow-sm placeholder:text-muted-foreground/70 focus-visible:ring-primary/25 dark:border-white/15 dark:bg-black/30";
const affiliateGhostButtonClass =
  "border border-border/60 bg-background/50 text-muted-foreground hover:bg-accent/50 hover:text-foreground dark:border-white/15 dark:bg-black/20";
const affiliateRowClass = "border-border/45 bg-background/50 dark:border-white/8 dark:bg-black/20";

const fmtDate = (s: string) => {
  try {
    return new Date(s).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

function GioiThieuPage() {
  const qc = useQueryClient();
  // refetchInterval: số dư đổi khi admin xử lý đơn rút (từ chối → hoàn tiền) — cập nhật không cần F5
  const me = useQuery({
    queryKey: ["affiliate-me"],
    queryFn: loadAffiliateMe,
    refetchInterval: 60_000,
  });
  // 🔴 Sổ hoa hồng phân trang 10 dòng/trang và LỌC Ở SERVER. Trước 25/08/2026 trang này
  // tải 200 dòng một lượt rồi lọc "Giới thiệu / Chiến dịch" trong bộ nhớ — vừa chậm vừa
  // cắt mất dòng thứ 201 trở đi mà không báo gì.
  const [commissionSource, setCommissionSource] = useState<CommissionSource>("all");
  const [commissionPage, setCommissionPage] = useState(1);
  const commissions = useQuery({
    queryKey: ["affiliate-commissions", { page: commissionPage, source: commissionSource }],
    queryFn: () =>
      getAffiliateCommissions(
        AFFILIATE_PAGE_SIZE,
        (commissionPage - 1) * AFFILIATE_PAGE_SIZE,
        commissionSource,
      ),
    placeholderData: keepPreviousData,
  });
  // Chỉ cần TỔNG số người giới thiệu cho tiêu đề thẻ — lấy đúng một trang là đủ.
  const referrals = useQuery({
    queryKey: ["affiliate-referrals"],
    queryFn: () => getAffiliateReferrals(AFFILIATE_PAGE_SIZE, 0),
  });
  const codes = useQuery({ queryKey: ["affiliate-campaign-codes"], queryFn: listCampaignCodes });

  const refetchAll = () => {
    qc.invalidateQueries({ queryKey: ["affiliate-me"] });
    qc.invalidateQueries({ queryKey: ["affiliate-campaign-codes"] });
    qc.invalidateQueries({ queryKey: ["affiliate-commissions"] });
    qc.invalidateQueries({ queryKey: ["affiliate-payouts"] });
  };

  const data = me.data;

  return (
    <main className="app-feature-page flex-1 min-h-0 overflow-y-auto px-3 py-4 pb-24 md:px-8 md:py-8">
      <div className="mx-auto max-w-3xl space-y-5">
        <header className="space-y-1">
          <h1 className="flex items-center gap-2 font-display text-2xl text-gold-gradient md:text-3xl">
            <Gift className="h-6 w-6 text-primary" /> Giới thiệu & Hoa hồng
          </h1>
          <p className="text-sm text-muted-foreground">
            Chia sẻ link của bạn — nhận <b className="text-primary">10% hoa hồng trọn đời</b> mỗi
            lần người được giới thiệu nạp tiền. Người được giới thiệu cũng được{" "}
            <b className="text-primary">+10%</b> lần nạp đầu.{" "}
            <Link
              to="/huong-dan-hoa-hong"
              className="text-primary underline-offset-2 hover:underline"
            >
              Xem hướng dẫn chi tiết →
            </Link>
          </p>
        </header>

        {me.isLoading || !data ? (
          <Skeleton className="h-40 w-full rounded-2xl" />
        ) : (
          <>
            <ReferralStatsCard
              data={data}
              referralsTotal={data.referral_count ?? referrals.data?.total ?? 0}
              onBound={() => qc.invalidateQueries({ queryKey: ["affiliate-me"] })}
            />
            <ReferralCodeCard
              data={data}
              onSaved={() => qc.invalidateQueries({ queryKey: ["affiliate-me"] })}
            />
            <BalancesCard data={data} onChanged={refetchAll} />
            <PayoutCard data={data} onChanged={refetchAll} />
            {data.campaign.can_create ? (
              <CampaignCard data={data} codes={codes.data?.items ?? []} onChanged={refetchAll} />
            ) : (
              <Card className="border-gold-soft/40 bg-card/60">
                <CardContent className="py-4 text-sm text-muted-foreground">
                  <Megaphone className="mr-2 inline h-4 w-4 text-primary/70" />
                  Đạt <b className="text-primary">{money(data.campaign.unlock_threshold)}</b> hoa
                  hồng tích luỹ để mở khoá <b>tạo mã chiến dịch</b> (tự đặt % khuyến mãi cho người
                  nạp).
                </CardContent>
              </Card>
            )}
            <CommissionsCard
              commissions={commissions.data?.items ?? []}
              total={commissions.data?.total ?? 0}
              referralsTotal={referrals.data?.total ?? 0}
              source={commissionSource}
              onSourceChange={(next) => {
                setCommissionSource(next);
                setCommissionPage(1);
              }}
              page={commissionPage}
              onPageChange={setCommissionPage}
              isFetching={commissions.isFetching}
            />
            <Card className="border-gold-soft/40 bg-card/60">
              <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
                <p className="text-sm text-muted-foreground">
                  <Megaphone className="mr-2 inline h-4 w-4 text-primary/70" />
                  Viết bài cảm nhận về Bói Đê trên mạng xã hội — bài được duyệt nhận thêm{" "}
                  <b className="text-primary">{money(1000000)}</b> vào ví.
                </p>
                <Button asChild size="sm" variant="outline" className="gap-1.5">
                  <Link to="/cam-nhan">Xem chương trình</Link>
                </Button>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </main>
  );
}

/**
 * Hai con số người dùng hỏi nhiều nhất: có bao nhiêu người đã gắn mã của mình, và
 * mình đang được giới thiệu bởi mã nào. Mã mình gắn là KHOÁ (set-once ở BE) nên chỉ
 * hiển thị, không có ô sửa.
 */
function ReferralStatsCard({
  data,
  referralsTotal,
  onBound,
}: {
  data: AffiliateMe;
  referralsTotal: number;
  onBound: () => void;
}) {
  const boundCode = data.referred_by_code ?? null;
  const hasReferrer = Boolean(boundCode || data.has_referrer);
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <Card className="border-gold-soft/40 bg-card/60">
        <CardContent className="py-4">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
            <Users className="h-3.5 w-3.5" /> Người đã gắn mã của bạn
          </div>
          <div className="mt-1 font-display text-2xl text-gold-gradient">
            {referralsTotal.toLocaleString("vi-VN")}
            <span className="ml-1.5 text-sm font-normal text-muted-foreground">người</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {data.referral_code
              ? "Tính mọi tài khoản đăng ký bằng mã hoặc link của bạn."
              : "Đặt mã bên dưới để bắt đầu giới thiệu."}
          </p>
        </CardContent>
      </Card>
      <Card className="border-gold-soft/40 bg-card/60">
        <CardContent className="py-4">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
            <Lock className="h-3.5 w-3.5" /> Mã bạn đang gắn
          </div>
          {hasReferrer ? (
            <>
              <div className="mt-1 break-all font-display text-2xl text-gold-gradient">
                {boundCode ?? "Đã gắn"}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Đã khoá, không thể thay đổi.
              </p>
            </>
          ) : (
            <BindReferrerForm onBound={onBound} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Ô gắn mã người giới thiệu cho tài khoản chưa có. Gắn NGAY (`POST /me/referrer`),
 * không đợi lượt nạp. Mã đến từ link (`?ref=`) thì điền sẵn và khoá, chỉ còn nút Gắn.
 */
function BindReferrerForm({ onBound }: { onBound: () => void }) {
  const [stored] = useState(() => getStoredReferralInfo());
  const [code, setCode] = useState(stored?.code ?? "");
  const locked = Boolean(stored?.locked);
  const bind = useMutation({
    mutationFn: () => bindMyReferrer(code),
    onSuccess: (res) => {
      clearStoredReferral();
      toast.success(`Đã gắn mã giới thiệu ${res.referred_by_code}`);
      onBound();
    },
    onError: (e) => {
      if (referrerErrorReason(e) === "already_bound") onBound();
      toast.error(e instanceof Error ? e.message : "Gắn mã thất bại");
    },
  });
  return (
    <div className="mt-2 space-y-2">
      <div className="flex gap-2">
        <Input
          value={code}
          onChange={(e) => !locked && setCode(e.target.value.replace(/\s+/g, "").toLowerCase())}
          readOnly={locked}
          placeholder="Nhập mã người giới thiệu"
          autoCapitalize="none"
          spellCheck={false}
          className={affiliateInputClass}
        />
        <Button
          onClick={() => bind.mutate()}
          disabled={bind.isPending || code.trim().length < 3}
          className="bg-gold-gradient text-primary-foreground"
        >
          {bind.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Gắn mã"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {locked
          ? "Mã từ link giới thiệu bạn đã bấm. Gắn một lần, không đổi được."
          : "Chưa gắn mã. Gắn một lần, không đổi được."}
      </p>
    </div>
  );
}

function ReferralCodeCard({ data, onSaved }: { data: AffiliateMe; onSaved: () => void }) {
  const [code, setCode] = useState(data.referral_code ?? "");
  const save = useMutation({
    mutationFn: () => setAffiliateCode(code.trim().toLowerCase()),
    onSuccess: (res) => {
      toast.success("Đã lưu mã giới thiệu");
      onSaved();
      void res;
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Lưu mã thất bại"),
  });
  const link = data.referral_link as string | null;

  return (
    <Card className="border-gold-soft/40 bg-card/60">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <BadgePercent className="h-4 w-4 text-primary" /> Mã giới thiệu của bạn
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toLowerCase())}
            placeholder="Đặt mã của bạn (vd: cuan-tiktok)"
            className={affiliateInputClass}
          />
          <Button
            onClick={() => save.mutate()}
            disabled={save.isPending || code.trim().length < 3}
            className="bg-gold-gradient text-primary-foreground"
          >
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Lưu mã"}
          </Button>
        </div>
        {link ? (
          <div className="flex items-center gap-2 rounded-xl border border-gold-soft/30 bg-background/40 px-3 py-2">
            <span className="min-w-0 flex-1 truncate text-sm text-foreground">{link}</span>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(link);
                toast.success("Đã copy link");
              }}
              className="shrink-0 text-primary transition hover:text-primary/70"
              title="Copy link"
            >
              <Copy className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Đặt mã để nhận link giới thiệu của riêng bạn.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function BalancesCard({ data, onChanged }: { data: AffiliateMe; onChanged: () => void }) {
  const [convertAmt, setConvertAmt] = useState("");

  const convert = useMutation({
    mutationFn: () => convertCommission(Number(convertAmt)),
    onSuccess: (res) => {
      toast.success(res.message);
      setConvertAmt("");
      onChanged();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Chuyển thất bại"),
  });

  return (
    <Card className="border-gold-soft/40 bg-card/60">
      <CardContent className="space-y-4 py-5">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-gold-soft/30 bg-background/40 p-3 text-center">
            <div className="flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
              <Wallet className="h-3.5 w-3.5" /> Ví bói
            </div>
            <div className="mt-1 font-display text-xl text-gold-gradient">
              {money(data.wallet_amount)}
            </div>
          </div>
          <div className="rounded-xl border border-primary/40 bg-gold-gradient/10 p-3 text-center">
            <div className="flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
              <Gift className="h-3.5 w-3.5" /> Hoa hồng (rút được)
            </div>
            <div className="mt-1 font-display text-xl text-gold-gradient">
              {money(data.commission_balance)}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Chuyển hoa hồng sang ví bói</Label>
          <div className="flex gap-2">
            <Input
              type="number"
              value={convertAmt}
              onChange={(e) => setConvertAmt(e.target.value)}
              placeholder="Số tiền"
              className={affiliateInputClass}
            />
            <Button
              variant="outline"
              onClick={() => convert.mutate()}
              disabled={convert.isPending || !Number(convertAmt)}
              className="gap-1.5 border-gold-soft/50"
            >
              <ArrowRightLeft className="h-4 w-4" /> Chuyển
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const PAYOUT_STATUS: Record<string, { label: string; cls: string }> = {
  pending: { label: "Chờ xử lý", cls: "bg-amber-500/15 text-amber-500" },
  paid: { label: "Đã chuyển", cls: "bg-emerald-500/15 text-emerald-500" },
  rejected: { label: "Từ chối · đã hoàn", cls: "bg-red-500/15 text-red-400" },
};

function PayoutCard({ data, onChanged }: { data: AffiliateMe; onChanged: () => void }) {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountName, setAccountName] = useState("");
  const prefilledRef = useRef(false);

  // refetchInterval: thấy trạng thái "Đã chuyển"/"Từ chối" ngay khi admin xử lý, không cần F5
  const payouts = useQuery({
    queryKey: ["affiliate-payouts"],
    queryFn: getAffiliatePayouts,
    refetchInterval: 30_000,
  });
  const items = payouts.data?.items ?? [];

  // Tài khoản đã lưu — BE tự lưu mỗi lần rút thành công
  const savedAccounts = useQuery({
    queryKey: ["affiliate-bank-accounts"],
    queryFn: getSavedBankAccounts,
  });
  const saved = savedAccounts.data?.items ?? [];

  const fillAccount = (acc: SavedBankAccount) => {
    setBankName(acc.bank_name);
    setAccountNumber(acc.bank_account_number);
    setAccountName(acc.bank_account_name);
  };

  // Tự điền tài khoản dùng gần nhất khi form còn trống (chỉ 1 lần)
  useEffect(() => {
    if (prefilledRef.current || saved.length === 0) return;
    if (bankName || accountNumber || accountName) return;
    prefilledRef.current = true;
    fillAccount(saved[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved]);

  const selectedId =
    saved.find(
      (a) =>
        a.bank_name === bankName &&
        a.bank_account_number === accountNumber &&
        a.bank_account_name === accountName,
    )?.id ?? null;

  const removeAccount = useMutation({
    mutationFn: (id: string) => deleteSavedBankAccount(id),
    onSuccess: () => {
      toast.success("Đã xoá tài khoản đã lưu");
      void queryClient.invalidateQueries({ queryKey: ["affiliate-bank-accounts"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Xoá thất bại"),
  });

  const payout = useMutation({
    mutationFn: () =>
      requestAffiliatePayout({
        amount: Number(amount),
        bank_name: bankName.trim(),
        bank_account_number: accountNumber.trim(),
        bank_account_name: accountName.trim().toUpperCase(),
      }),
    onSuccess: (res) => {
      toast.success(res.message);
      setAmount("");
      void queryClient.invalidateQueries({ queryKey: ["affiliate-bank-accounts"] });
      onChanged();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Yêu cầu rút thất bại"),
  });

  const formValid =
    Number(amount) >= data.min_payout &&
    Number(amount) <= data.commission_balance &&
    bankName.trim().length >= 2 &&
    accountNumber.trim().length >= 4 &&
    accountName.trim().length >= 2;

  return (
    <Card className="border-gold-soft/40 bg-card/60">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Landmark className="h-4 w-4 text-primary" /> Rút tiền hoa hồng
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Tối thiểu <b className="text-primary">{money(data.min_payout)}</b>. Gửi yêu cầu kèm tài
          khoản ngân hàng — admin sẽ chuyển khoản trong <b>1–3 ngày làm việc</b>. Thông tin tài
          khoản sẽ được lưu lại để lần sau dùng lại.
        </p>
        {saved.length > 0 ? (
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">
              Tài khoản đã lưu — bấm để dùng lại
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {saved.map((acc) => (
                <div
                  key={acc.id}
                  className={`flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition ${
                    selectedId === acc.id
                      ? "border-primary/60 bg-primary/10 text-foreground"
                      : `${affiliateRowClass} text-muted-foreground hover:border-primary/40 hover:text-foreground`
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => fillAccount(acc)}
                    className="min-w-0 truncate"
                    title={`${acc.bank_name} · ${acc.bank_account_number} · ${acc.bank_account_name}`}
                  >
                    <span className="font-medium">{acc.bank_name}</span> · {acc.bank_account_number}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeAccount.mutate(acc.id)}
                    disabled={removeAccount.isPending}
                    className="shrink-0 rounded-full p-0.5 text-muted-foreground/60 transition hover:text-red-400"
                    title="Xoá tài khoản này"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : null}
        <div className="grid grid-cols-2 gap-2">
          <Input
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            placeholder="Ngân hàng (vd: Vietcombank)"
            className={affiliateInputClass}
          />
          <Input
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value.replace(/[^0-9a-zA-Z]/g, ""))}
            placeholder="Số tài khoản"
            className={affiliateInputClass}
          />
        </div>
        <Input
          value={accountName}
          onChange={(e) => setAccountName(e.target.value.toUpperCase())}
          placeholder="Tên chủ tài khoản (vd: NGUYEN VAN A)"
          className={affiliateInputClass}
        />
        <div className="flex gap-2">
          <Input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={`Số tiền rút (tối thiểu ${money(data.min_payout)})`}
            className={affiliateInputClass}
          />
          <Button
            onClick={() => payout.mutate()}
            disabled={payout.isPending || !formValid}
            className="shrink-0 bg-gold-gradient text-primary-foreground"
          >
            {payout.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Gửi yêu cầu"}
          </Button>
        </div>

        {items.length > 0 ? (
          <div className="space-y-1.5 pt-1">
            <Label className="text-xs text-muted-foreground">Đơn rút của bạn</Label>
            <ul className="space-y-1.5">
              {items.map((p: AffiliatePayout) => {
                const st = PAYOUT_STATUS[p.status] ?? {
                  label: p.status,
                  cls: "bg-muted text-muted-foreground",
                };
                return (
                  <li
                    key={p.id}
                    className={`rounded-lg border px-3 py-2 text-xs ${affiliateRowClass}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-foreground">{money(p.amount)}</span>
                      <span className={`rounded-full px-2 py-0.5 font-medium ${st.cls}`}>
                        {st.label}
                      </span>
                    </div>
                    <div className="mt-0.5 flex items-center justify-between gap-2 text-muted-foreground">
                      <span className="min-w-0 truncate">
                        {p.bank_name} · {p.bank_account_number}
                      </span>
                      <span className="shrink-0 text-muted-foreground/60">
                        {fmtDate(p.created_at)}
                      </span>
                    </div>
                    {p.note ? (
                      <div className="mt-0.5 text-muted-foreground/80">{p.note}</div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

type CodeFilter = "all" | "active" | "ended";

const CODE_FILTERS: { key: CodeFilter; label: string }[] = [
  { key: "all", label: "Tất cả" },
  { key: "active", label: "Đang chạy" },
  { key: "ended", label: "Đã hết" },
];

function CampaignCard({
  data,
  codes,
  onChanged,
}: {
  data: AffiliateMe;
  codes: CampaignCode[];
  onChanged: () => void;
}) {
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [percent, setPercent] = useState(10);
  const [usage, setUsage] = useState(10);
  const [codeFilter, setCodeFilter] = useState<CodeFilter>("all");

  const filteredCodes = useMemo(() => {
    if (codeFilter === "active") return codes.filter((c) => c.active);
    if (codeFilter === "ended") return codes.filter((c) => !c.active);
    return codes;
  }, [codes, codeFilter]);

  const maxPercent = Math.round(data.campaign.max_promo_rate * 100);
  const available = data.campaign.budget_available as number;
  const maxUsage = percent > 0 ? Math.floor(available / percent) : 0;

  const create = useMutation({
    mutationFn: () =>
      createCampaignCode({
        code: code.trim().toLowerCase(),
        label: label.trim() || undefined,
        promo_rate: percent / 100,
        usage_limit: usage,
      }),
    onSuccess: () => {
      toast.success("Đã tạo mã chiến dịch");
      setCode("");
      setLabel("");
      onChanged();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Tạo mã thất bại"),
  });

  const cost = useMemo(() => percent * usage, [percent, usage]);

  return (
    <Card className="border-gold-soft/40 bg-card/60">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Megaphone className="h-4 w-4 text-primary" /> Mã chiến dịch
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Ngân sách còn lại: <b className="text-primary">{available.toFixed(0)}</b> điểm (% × số
          lượt). Bạn hưởng cố định <b>{Math.round(data.campaign.owner_rate * 100)}%</b>/lượt nạp.
        </p>
        <p className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-[11px] text-muted-foreground">
          💡 Người nạp nhập mã này vào ô <b className="text-primary">"Mã giới thiệu"</b> ở trang nạp
          tiền để được khuyến mãi.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toLowerCase())}
            placeholder="Mã (vd: he2026)"
            className={affiliateInputClass}
          />
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Tên chiến dịch"
            className={affiliateInputClass}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">
              Khuyến mãi cho người nạp: {percent}%
            </Label>
            <input
              type="range"
              min={1}
              max={maxPercent}
              value={percent}
              onChange={(e) => setPercent(Number(e.target.value))}
              className="mt-2 w-full accent-[var(--primary)]"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Số lượt (tối đa {maxUsage})</Label>
            <Input
              type="number"
              value={usage}
              max={maxUsage}
              onChange={(e) => setUsage(Math.max(1, Number(e.target.value)))}
              className={`mt-1 ${affiliateInputClass}`}
            />
          </div>
        </div>
        <Button
          onClick={() => create.mutate()}
          disabled={create.isPending || code.trim().length < 3 || cost > available || usage < 1}
          className="w-full bg-gold-gradient text-primary-foreground"
        >
          {create.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Tạo mã (tốn {cost} / {available.toFixed(0)} điểm)
        </Button>

        {codes.length > 0 ? (
          <div className="space-y-2 pt-1">
            <div className="flex gap-1.5">
              {CODE_FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setCodeFilter(f.key)}
                  className={`rounded-full px-3 py-1 text-xs transition ${
                    codeFilter === f.key
                      ? "bg-gold-gradient font-medium text-primary-foreground"
                      : affiliateGhostButtonClass
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {filteredCodes.length === 0 ? (
              <p className="py-3 text-center text-xs text-muted-foreground">
                Không có mã nào khớp bộ lọc.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {filteredCodes.map((c) => (
                  <li
                    key={c.id}
                    className={`flex items-center justify-between rounded-lg border px-3 py-2 text-xs ${affiliateRowClass}`}
                  >
                    <span className="font-medium text-foreground">
                      {c.code}{" "}
                      <span className="text-muted-foreground">
                        · {Math.round(c.promo_rate * 100)}%
                      </span>
                    </span>
                    <span className="text-muted-foreground">
                      {c.usage_count}/{c.usage_limit ?? "∞"} lượt {c.active ? "" : "· đã hết"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

type CommissionFilter = "all" | "referral" | "campaign";

const COMMISSION_FILTERS: { key: CommissionFilter; label: string }[] = [
  { key: "all", label: "Tất cả" },
  { key: "referral", label: "Giới thiệu" },
  { key: "campaign", label: "Chiến dịch" },
];

/**
 * Sổ hoa hồng — **lọc và phân trang chạy ở SERVER**.
 *
 * ⚠️ `commissions` là MỘT TRANG đã lọc sẵn, đừng lọc lại ở đây: bộ lọc client trên 10
 * dòng sẽ làm tab "Chiến dịch" trống trơn với người có sổ hoa hồng dài.
 */
function CommissionsCard({
  commissions,
  total,
  referralsTotal,
  source,
  onSourceChange,
  page,
  onPageChange,
  isFetching,
}: {
  commissions: AffiliateCommission[];
  total: number;
  referralsTotal: number;
  source: CommissionFilter;
  onSourceChange: (source: CommissionFilter) => void;
  page: number;
  onPageChange: (page: number) => void;
  isFetching: boolean;
}) {
  const filter = source;
  const pageItems = commissions;
  const totalPages = Math.max(1, Math.ceil(total / LOGS_PER_PAGE));
  const safePage = Math.min(page, totalPages);

  const changeFilter = (f: CommissionFilter) => onSourceChange(f);

  return (
    <Card className="border-gold-soft/40 bg-card/60">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="h-4 w-4 text-primary" /> Hoạt động ({referralsTotal} người giới thiệu)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-1.5">
          {COMMISSION_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => changeFilter(f.key)}
              className={`rounded-full px-3 py-1 text-xs transition ${
                filter === f.key
                  ? "bg-gold-gradient font-medium text-primary-foreground"
                  : affiliateGhostButtonClass
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {pageItems.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            {filter === "all"
              ? "Chưa có hoa hồng. Hãy chia sẻ link của bạn!"
              : "Không có hoạt động nào khớp bộ lọc."}
          </p>
        ) : (
          <>
            <ul className="space-y-1.5">
              {pageItems.map((c, i) => (
                <li
                  key={`${c.order_code}-${i}`}
                  className="flex items-center justify-between gap-2 rounded-lg border border-border/40 bg-background/40 px-3 py-2 text-sm"
                >
                  <span className="min-w-0">
                    <span className="text-muted-foreground">
                      {c.source === "campaign" ? "Chiến dịch" : "Giới thiệu"}
                      {c.code ? ` · ${c.code}` : ""}
                    </span>
                    {c.created_at ? (
                      <span className="ml-2 text-[11px] text-muted-foreground/60">
                        {fmtDate(c.created_at)}
                      </span>
                    ) : null}
                  </span>
                  <span className="shrink-0 font-semibold text-primary">
                    +{money(c.commission)}
                  </span>
                </li>
              ))}
            </ul>

            {totalPages > 1 ? (
              <div className="flex items-center justify-between pt-1 text-xs text-muted-foreground">
                <button
                  type="button"
                  disabled={safePage <= 1 || isFetching}
                  onClick={() => onPageChange(safePage - 1)}
                  className="rounded-md border border-border/60 bg-background/40 px-3 py-1 transition hover:text-foreground disabled:opacity-40 dark:border-white/15 dark:bg-transparent"
                >
                  Trước
                </button>
                <span>
                  Trang {safePage}/{totalPages} · {total} dòng
                </span>
                <button
                  type="button"
                  disabled={safePage >= totalPages || isFetching}
                  onClick={() => onPageChange(safePage + 1)}
                  className="rounded-md border border-border/60 bg-background/40 px-3 py-1 transition hover:text-foreground disabled:opacity-40 dark:border-white/15 dark:bg-transparent"
                >
                  Sau
                </button>
              </div>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}
